import React, {
 useContext, useEffect, useRef
} from 'react';
import {
 User, Bot
} from 'lucide-react';
import {
 TypingLoader
} from 'shared/components/TypingLoader';
import {
 Context
} from 'shared/context/GlobalState';
import MessageParser from './MessageParser';
import VoiceMessage from './VoiceMessage';
import {
 WebChatConfigType
} from '../../../types/builderChannel.types'; // adjust import path if needed

interface Message {
  type: 'user' | 'bot';
  text: string;
  isAudio?: boolean;
  messageType?: 'text' | 'voice';
  audioBlob?: Blob;
  duration?: number;
}

interface WebchatBodyProps {
  messages: Message[];
  loading: boolean;
  emitMessage: (msg: string) => void;
  chatSettings: WebChatConfigType & {
    chatGreetingMessage: string;
    secondaryColor: string;
    textSecondaryColor: string;
    chatHeight: number;
    conversationStarters: string[];
  };
  isHome: boolean;
}

const WebchatBody: React.FC<WebchatBodyProps> = ({
  messages,
  loading,
  emitMessage,
  chatSettings,
  isHome,
}) => {
  const chatBody = useRef<HTMLDivElement | null>(null);

  const {
 addNewNotifcation
} = useContext(Context);

  const {
      chatGreetingMessage,
      primaryColor,
      textPrimaryColor,
      secondaryColor,
      textSecondaryColor,
      tertiaryColor,
      aiIconColor,
      textTertiaryColor,
      chatHeight,
      conversationStarters,
      userIconColor,
  } = chatSettings;

  const messageStyle =
    'max-w-[75%] px-3 py-2 rounded-lg text-sm leading-[1.6]';
  const iconsStyle =
    'inline-flex items-center justify-center w-[35px] h-[35px] rounded-full text-lg';

  useEffect(() => {
    chatBody.current?.scrollTo({
      top: chatBody.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isHome]);

  const handleStarterClick = (starter: string) => {
    if (loading) {
      addNewNotifcation(
        'Please wait untill the response comes.',
        'warning'
      );
      return;
    }
    emitMessage(starter);
  };

  const scrollbarStyles = `
    .webchat-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .webchat-scrollbar::-webkit-scrollbar-track {
      background: ${primaryColor};
    }
    .webchat-scrollbar::-webkit-scrollbar-thumb {
      background-color: ${secondaryColor};
      border-radius: 20px;
      border: 2px solid ${primaryColor};
    }
  `;

  return (
      <>
          <style>{scrollbarStyles}</style>
          <div
        className="overflow-y-auto p-4 webchat-scrollbar"
        style={{
          height: `${chatHeight}px`
        }}
        ref={chatBody}
        data-no-drag
      >
              {isHome ? (
                  <div>
                      <p className="text-2xl font-light mb-6">{chatGreetingMessage}</p>
                      <div className="space-y-3">
                          {conversationStarters.map((starter, index) => (
                              <button
                key={index}
                className="w-full text-left p-2 rounded-full transition-colors"
                style={{
                  backgroundColor: secondaryColor,
                  color: textSecondaryColor,
                }}
                onClick={() => handleStarterClick(starter)}
              >
                                  {starter}
                              </button>
            ))}
                      </div>
                  </div>
      ) : (
          <>
              {messages.map((msg, index) => (
                  <div
              key={index}
              className={`flex my-3 gap-2 ${
                msg.type === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
                      <span
                className={iconsStyle}
                style={{
                  backgroundColor: msg.type === 'user' ? secondaryColor : (tertiaryColor as string) || primaryColor,
                  color: msg.type === 'user' ? (userIconColor as string) || textSecondaryColor : msg.type === 'bot' ? (aiIconColor as string) || (textTertiaryColor as string) || textPrimaryColor : (textTertiaryColor as string) || textPrimaryColor,
                  border: 'none',
                  boxShadow: msg.type === 'bot' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                          {msg.type === 'user' ? (
                              <User strokeWidth={1.5} />
                ) : (
                    <Bot strokeWidth={1.5} />
                )}
                      </span>

                      <div
                className={`${messageStyle} overflow-hidden`}
                style={{
                  backgroundColor: msg.type === 'user' ? secondaryColor : (tertiaryColor as string) || primaryColor,
                  color: msg.type === 'user' ? textSecondaryColor : (textTertiaryColor as string) || textPrimaryColor,
                  border: 'none',
                  boxShadow: msg.type === 'bot' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                          {msg.messageType === 'voice' ? (
                              <VoiceMessage
                                  audioBlob={msg.audioBlob!}
                                  duration={msg.duration || 0}
                                  isUser={msg.type === 'user'}
                                  accentColor={chatSettings.accentColor || 'black'}
                              />
                          ) : (
                              <MessageParser text={msg.text} />
                          )}
                      </div>
                  </div>
          ))}
              {loading && (
              <div className="flex my-2 justify-start gap-2">
                  <span className={iconsStyle} style={{
                    backgroundColor: (tertiaryColor as string) || primaryColor,
                    color: (aiIconColor as string) || (textTertiaryColor as string) || textPrimaryColor,
                    border: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}>
                      <Bot strokeWidth={1.5} />
                  </span>
                  <TypingLoader>
                      <div></div>
                      <div></div>
                      <div></div>
                  </TypingLoader>
              </div>
          )}
          </>
      )}
          </div>
      </>
  );
};

export default WebchatBody;
